/**
 * Metadata some HTTP clients attach instead of a real `Response`.
 */
export interface AttachedHttpMetadata {
  status?: number;
  statusText?: string;
  body?: unknown;
}

/**
 * Errors that may carry `response` and/or `body` (fetch wrappers, HTTP libraries, ApiError).
 */
export type ErrorWithHttpAttachments = Error & {
  response?: Response | AttachedHttpMetadata | unknown;
  body?: unknown;
};

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const hasHttpAttachments = (err: Error): err is ErrorWithHttpAttachments =>
  'response' in err || 'body' in err;

export const isAttachedHttpMetadata = (
  value: unknown,
): value is AttachedHttpMetadata =>
  isRecord(value) && ('status' in value || 'statusText' in value);

/**
 * Returns `response` or `body` from an unknown caught value (for handlers/logging).
 */
export const getErrorResponseAttachment = (err: unknown): unknown => {
  if (typeof err !== 'object' || err === null) {
    return undefined;
  }
  const candidate = err as { response?: unknown; body?: unknown };
  return candidate.response ?? candidate.body;
};

export const responseBodyToText = (body: unknown): string | undefined => {
  if (body === undefined) {
    return undefined;
  }
  if (typeof body === 'string') {
    return body;
  }
  try {
    return JSON.stringify(body, null, 2);
  } catch {
    return undefined;
  }
};
